/**
 * Bot director — a heuristic AI that directs a match. Used for automated
 * balancing (skill 0.9 → ~A, 0.5 → ~C, 0.1 → ~F) and attract-mode demos.
 *
 * Skill scales: reaction time, how often it notices reaction moments, replays,
 * graphics and booth requests, cut discipline, and how often it makes
 * amateur mistakes (random cuts, jumpy bursts, forgetting the scorebug,
 * clutter during attacks).
 *
 * BotAction is structurally a subset of director/actions.ts DirectorAction, so
 * bot output can be fed straight into applyDirectorAction.
 */
import type {
  CameraFrame, CameraKind, CommentatorRequest, DirectorState, GraphicKind, MatchEvent,
  MatchEventType, MatchPhase, Rng, WorldSnapshot,
} from '../core/contracts';
import { type CamKindOf, inferCamKind, isWideSize } from './cameras';

export type BotAction =
  | { type: 'hot_punch'; camId: string }
  | { type: 'replay_roll'; eventId: number | null }
  | { type: 'replay_angle'; camId: string }
  | { type: 'replay_end' }
  | { type: 'graphic_show'; kind: GraphicKind; subjectId?: string }
  | { type: 'graphic_hide'; target: GraphicKind };

export interface BotInput {
  snapshot: WorldSnapshot;
  newEvents: MatchEvent[];
  frames: CameraFrame[];
  requests: CommentatorRequest[];
  director: DirectorState;
}

export interface BotDirector {
  readonly skill: number;
  decide(input: BotInput): BotAction[];
}

export interface BotOptions { camKindOf?: CamKindOf }

const LIVE: ReadonlySet<MatchPhase> = new Set<MatchPhase>(['open_play', 'kickoff', 'set_piece']);
const STOPPAGE: ReadonlySet<MatchPhase> = new Set<MatchPhase>(['stoppage', 'goal_celebration']);
const REACTION: ReadonlySet<MatchEventType> = new Set<MatchEventType>([
  'celebration', 'yellow_card', 'red_card', 'injury', 'manager_reaction', 'substitution',
]);
const GOALMOUTH: ReadonlySet<MatchEventType> = new Set<MatchEventType>(['shot', 'shot_on_target', 'save', 'goal', 'near_miss', 'woodwork', 'penalty']);
const EVENT_GFX: Partial<Record<MatchEventType, GraphicKind>> = {
  goal: 'goal_lower_third', yellow_card: 'card_lower_third', red_card: 'card_lower_third', substitution: 'sub_lower_third',
};

type Run = (inp: BotInput) => BotAction[] | 'defer';
interface Plan { at: number; run: Run; tag: string }

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
/** Ball in play, or a dangerous set piece (corner, penalty, direct free kick) about to be taken. */
const inPlay = (s: WorldSnapshot) => s.phase === 'open_play' || (LIVE.has(s.phase) && s.intensity > 0.45);

export function createBotDirector(skill: number, rng: Rng, opts: BotOptions = {}): BotDirector {
  const k = Math.max(0, Math.min(1, skill));
  const kindOf: CamKindOf = opts.camKindOf ?? inferCamKind;
  const minGap = lerp(0.5, 2.2, k);
  const sloppy = 1 - k;
  /** Diligence: how reliably the bot remembers the jobs (replays, graphics, booth, reactions). Novices forget most. */
  const dil = k ** 1.3;

  let plans: Plan[] = [];
  let lastCutT = -Infinity;
  let holdUntil = -Infinity;
  let lastPhase: MatchPhase | null = null;
  let lastT: number | null = null;
  let nextVarietyAt = 15;
  let lastRestartT = -Infinity;
  let scorebugForgotUntil = -Infinity;
  let replayAngleAt = Infinity, replayEndAt = Infinity, replayAngleCam: string | null = null;
  let replayWasActive = false;
  let stoppageRotation = 0;
  let panicCooldown = 0;
  const worthy: MatchEvent[] = [];
  const replayed = new Set<number>();
  const seenReq = new Set<number>();

  const react = () => lerp(1.5, 0.15, k) + rng.range(0, 0.6) * (1 - k);
  const schedule = (at: number, tag: string, run: Run) => plans.push({ at, run, tag });
  const camOf = (frames: CameraFrame[], kind: CameraKind) => frames.find((f) => kindOf(f.camId) === kind)?.camId ?? null;

  function tryCut(inp: BotInput, camId: string | null, force = false): BotAction[] | 'defer' {
    if (!camId) return [];
    const t = inp.snapshot.t;
    if (inp.director.replay.active) return [];
    if (camId === inp.director.programCamId) return [];
    if (!force && t - lastCutT < minGap) return 'defer';
    lastCutT = t;
    return [{ type: 'hot_punch', camId }];
  }

  /** Best camera for an event's ideal framing (prefers handhelds for reactions). */
  function bestFor(inp: BotInput, ev: MatchEvent): string | null {
    const frames = inp.frames;
    if (ev.type === 'manager_reaction') return camOf(frames, 'bench');
    if (ev.type === 'crowd_moment') return camOf(frames, 'crowd');
    const subject = ev.ideal.subjectId ?? ev.playerId;
    const ok = frames.filter((f) => ev.ideal.shotSizes.includes(f.framing.shotSize)
      && kindOf(f.camId) !== 'reverse_angle'
      && (!subject || f.framing.subjectId === subject || f.framing.visible.includes(subject)));
    const score = (f: CameraFrame) => {
      const kd = kindOf(f.camId);
      const hand = kd === 'pitchside_handheld_left' || kd === 'pitchside_handheld_right' ? 1 : 0;
      return hand + f.framing.quality + (ev.ideal.shotSizes[0] === f.framing.shotSize ? 0.5 : 0);
    };
    ok.sort((a, b) => score(b) - score(a));
    return ok[0]?.camId ?? null;
  }

  function wideCam(inp: BotInput): string | null {
    const s = inp.snapshot;
    // Skilled directors use the high-18 cams for attacks in the final third.
    if (k > 0.6 && s.intensity > 0.65 && Math.abs(s.focus.x) > 30) {
      const kind: CameraKind = s.focus.x > 0 ? 'high_18_right' : 'high_18_left';
      const f = inp.frames.find((x) => kindOf(x.camId) === kind);
      if (f && f.framing.focusInFrame && isWideSize(f.framing.shotSize)) return f.camId;
    }
    return camOf(inp.frames, 'main_wide');
  }

  function rollReplay(inp: BotInput, ev: MatchEvent | null, playFor: number): BotAction[] {
    if (inp.director.replay.active) return [];
    if (ev) replayed.add(ev.id);
    const t = inp.snapshot.t;
    replayEndAt = t + playFor;
    replayAngleCam = null;
    replayAngleAt = Infinity;
    if (rng.chance(lerp(0.05, 0.95, dil))) {
      replayAngleAt = t + playFor * rng.range(0.4, 0.55);
      const order: CameraKind[] = ev && GOALMOUTH.has(ev.type)
        ? ['reverse_angle', ev.pos.x >= 0 ? 'behind_goal_right' : 'behind_goal_left', 'main_tight']
        : ['reverse_angle', 'main_tight', 'main_wide'];
      for (const kd of order) {
        const c = camOf(inp.frames, kd);
        if (c && c !== inp.director.replay.camId) { replayAngleCam = c; break; }
      }
    }
    return [{ type: 'replay_roll', eventId: ev?.id ?? null }];
  }

  function showGraphic(inp: BotInput, kind: GraphicKind, subjectId: string | undefined, holdFor: number): BotAction[] | 'defer' {
    const s = inp.snapshot;
    // Good directors wait for a lull before putting anything over live play.
    if (inPlay(s) && s.intensity > 0.5 && rng.chance(k)) return 'defer';
    schedule(s.t + holdFor, 'hide', () => [{ type: 'graphic_hide', target: kind }]);
    const a: BotAction = subjectId ? { type: 'graphic_show', kind, subjectId } : { type: 'graphic_show', kind };
    return [a];
  }

  function onEvents(inp: BotInput): void {
    const t = inp.snapshot.t;
    for (const ev of inp.newEvents) {
      if (ev.ideal.replayWorthy) worthy.push(ev);
      if (REACTION.has(ev.type) && rng.chance(lerp(0.12, 1, dil))) {
        schedule(ev.t + react(), 'reaction', (i) => {
          if (i.director.replay.active) return [];
          // Good directors stay on the action while the ball is live; novices bail to the dugout.
          if (inPlay(i.snapshot) && rng.chance(k)) return [];
          const r = tryCut(i, bestFor(i, ev));
          if (r !== 'defer' && r.length > 0) holdUntil = i.snapshot.t + rng.range(3, 4.5);
          return r;
        });
      }
      if (ev.type === 'half_time' && rng.chance(lerp(0.1, 1, dil))) {
        schedule(ev.t + lerp(9, 1.5, k), 'gfx', () => [{ type: 'graphic_show', kind: 'half_time_score' }]);
      }
      const gfx = EVENT_GFX[ev.type];
      if (gfx && rng.chance(lerp(0.1, 1, dil))) {
        const subject = ev.playerId ?? undefined;
        schedule(ev.t + lerp(14, 3.5, k) + rng.range(0, 2), 'gfx', (i) => showGraphic(i, gfx, subject, 6));
      }
    }
    while (worthy.length > 0 && t - worthy[0]!.t > 60) worthy.shift();
  }

  function onRequests(inp: BotInput): void {
    for (const req of inp.requests) {
      if (seenReq.has(req.id)) continue;
      seenReq.add(req.id);
      if (!rng.chance(lerp(0.08, 1, dil))) continue;
      const at = req.t + lerp(10, 1.2, k) + rng.range(0, 2) * (1 - k);
      const w = req.wants;
      schedule(at, 'booth', (i) => {
        const s = i.snapshot;
        if (w.graphic) return showGraphic(i, w.graphic, w.subjectId, 5);
        if (w.camKind) {
          if (inPlay(s) && s.intensity > 0.45 && rng.chance(k)) return 'defer';
          if (i.director.replay.active) return 'defer';
          const r = tryCut(i, camOf(i.frames, w.camKind));
          if (r !== 'defer' && r.length > 0) holdUntil = s.t + rng.range(2.5, 3.5);
          return r;
        }
        if (w.replay) {
          // A quick one is fine while play is being knocked about at the back; not during an attack.
          if (inPlay(s) && s.intensity > 0.3 && rng.chance(k)) return 'defer';
          const ev = [...worthy].reverse().find((e) => s.t - e.t < 30) ?? null;
          return rollReplay(i, ev, inPlay(s) ? rng.range(3.5, 4.5) : rng.range(5, 7));
        }
        return [];
      });
    }
  }

  function onPhase(inp: BotInput): void {
    const s = inp.snapshot;
    const ph = s.phase;
    if (lastPhase === null || ph === lastPhase) { lastPhase = ph; return; }
    const from = lastPhase;
    lastPhase = ph;
    if (STOPPAGE.has(ph) && !STOPPAGE.has(from)) {
      const cand = [...worthy].reverse().find((e) => !replayed.has(e.id) && s.t - e.t < 20);
      if (cand && rng.chance(lerp(0.04, 1, dil))) {
        // Let the celebration / reaction shot breathe first.
        const wait = cand.type === 'goal' ? Math.max(0, cand.t + 9.5 - s.t) : react() + lerp(3, 1, k);
        schedule(s.t + wait, 'replay', (i) => {
          if (inPlay(i.snapshot)) return [];
          if (i.snapshot.t < holdUntil) return 'defer';
          return rollReplay(i, cand, rng.range(5.5, 7.5));
        });
      }
    }
    // Ball back in play (kickoff taken / set piece struck): out of any replay, back to a covering wide.
    if (ph === 'open_play') {
      lastRestartT = s.t;
      const delay = lerp(3.2, 0.1, k) + rng.range(0, 0.3);
      schedule(s.t + delay, 'restart', (i) => {
        const acts: BotAction[] = [];
        if (i.director.replay.active) { acts.push({ type: 'replay_end' }); replayEndAt = Infinity; replayAngleAt = Infinity; }
        holdUntil = -Infinity;
        const wide = wideCam(i);
        if (wide && wide !== i.director.programCamId) { acts.push({ type: 'hot_punch', camId: wide }); lastCutT = i.snapshot.t; }
        return acts;
      });
    }
  }

  return {
    skill: k,
    decide(inp) {
      const s = inp.snapshot;
      const t = s.t;
      const dt = lastT === null ? 0 : Math.max(0, t - lastT);
      lastT = t;
      const d = inp.director;
      const out: BotAction[] = [];

      onEvents(inp);
      onRequests(inp);
      onPhase(inp);

      // Scheduled plans.
      const due = plans.filter((p) => p.at <= t);
      plans = plans.filter((p) => p.at > t);
      for (const p of due) {
        const r = p.run(inp);
        if (r === 'defer') {
          if (t - p.at < 15) plans.push({ ...p, at: Math.max(t + 0.3, lastCutT + minGap) });
        } else out.push(...r);
      }

      // Replay management.
      if (d.replay.active && !replayWasActive && replayEndAt === Infinity) replayEndAt = t + 6;
      if (d.replay.active) {
        if (replayAngleCam && t >= replayAngleAt) {
          out.push({ type: 'replay_angle', camId: replayAngleCam });
          replayAngleCam = null; replayAngleAt = Infinity;
        }
        const danger = inPlay(s) && s.intensity > 0.45 && rng.chance(k * dt * 3);
        if (t >= replayEndAt || danger) {
          out.push({ type: 'replay_end' });
          replayEndAt = Infinity; replayAngleAt = Infinity; replayAngleCam = null;
          lastCutT = t;
        }
      }
      replayWasActive = d.replay.active;
      const inReplay = d.replay.active || out.some((a) => a.type === 'replay_roll');
      const cutThisTick = out.some((a) => a.type === 'hot_punch');

      // Scorebug.
      const bugOn = d.graphics.some((g) => g.kind === 'scorebug' && g.hiddenAt === undefined);
      if (!bugOn && s.phase !== 'pre_match' && t >= scorebugForgotUntil && rng.chance(dt * lerp(0.05, 3, k))) {
        out.push({ type: 'graphic_show', kind: 'scorebug' });
      } else if (bugOn && LIVE.has(s.phase) && rng.chance(dt * sloppy ** 2 * 0.04)) {
        out.push({ type: 'graphic_hide', target: 'scorebug' });
        scorebugForgotUntil = t + lerp(70, 5, k);
      }
      // Clear the lower-third area when an attack builds (skilled directors notice quickly).
      if (inPlay(s) && s.intensity > 0.55 && rng.chance(Math.min(1, dt * lerp(0.2, 6, k)))
        && d.graphics.some((g) => g.hiddenAt === undefined && g.kind !== 'scorebug' && g.kind !== 'clock' && g.kind !== 'replay_bug')) {
        for (const g of d.graphics) {
          if (g.hiddenAt === undefined && g.kind !== 'scorebug' && g.kind !== 'clock' && g.kind !== 'replay_bug') out.push({ type: 'graphic_hide', target: g.kind });
        }
      }
      // Amateur clutter: stats up in the middle of an attack.
      if (LIVE.has(s.phase) && s.intensity > 0.6 && rng.chance(dt * sloppy * 0.03)) {
        out.push({ type: 'graphic_show', kind: 'match_stats' });
        schedule(t + rng.range(4, 8), 'hide', () => [{ type: 'graphic_hide', target: 'match_stats' }]);
      }

      if (inReplay || cutThisTick || t < holdUntil) return out;
      const pgm = inp.frames.find((f) => f.camId === d.programCamId);

      if (LIVE.has(s.phase)) {
        // Amateur mistakes.
        // Panic: novices bail to a random close-up just as an attack peaks.
        if (s.intensity > 0.7 && t > panicCooldown) {
          panicCooldown = t + 12;
          if (rng.chance(Math.min(1, sloppy * 1.1))) {
            const closeCams = inp.frames.filter((f) => f.framing.shotSize === 'close' || kindOf(f.camId) === 'bench' || kindOf(f.camId) === 'crowd');
            if (closeCams.length > 0) {
              const r = tryCut(inp, rng.pick(closeCams).camId, true);
              if (r !== 'defer' && r.length > 0) { out.push(...r); holdUntil = t + rng.range(2, 5); }
              return out;
            }
          }
        }
        if (rng.chance(dt * (0.12 * sloppy + 0.2 * sloppy ** 3))) {
          const f = rng.pick(inp.frames);
          const r = tryCut(inp, f.camId, true);
          if (r !== 'defer' && r.length > 0) { out.push(...r); holdUntil = t + rng.range(2, 10) * (0.4 + sloppy); }
          return out;
        }
        if (rng.chance(dt * sloppy * 0.05)) {
          for (let i = 1; i <= 3; i++) {
            schedule(t + i * 0.5, 'jumpy', (inp2) => tryCut(inp2, rng.pick(inp2.frames).camId, true));
          }
          return out;
        }
        const wide = wideCam(inp);
        const covering = pgm && (pgm.framing.focusInFrame || pgm.framing.ballInFrame);
        const pgmKind = kindOf(d.programCamId);
        const onWide = pgm && isWideSize(pgm.framing.shotSize) && pgmKind !== 'reverse_angle' && covering;
        if (!onWide) {
          // Back to a covering wide shot (reaction-time delayed by skill).
          if (rng.chance(1 - Math.exp(-dt / Math.max(0.05, react() * lerp(3, 0.7, k))))) {
            const r = tryCut(inp, wide);
            if (r !== 'defer') out.push(...r);
          }
        } else if (wide && wide !== d.programCamId && rng.chance(dt * 2 * k)) {
          const r = tryCut(inp, wide);
          if (r !== 'defer') out.push(...r);
        } else if (t >= nextVarietyAt && s.phase === 'open_play' && t - lastRestartT > 4 && s.intensity < 0.45 && t - lastCutT > 8) {
          // Rhythm: a short tight shot on the ball carrier during calm build-up.
          nextVarietyAt = t + rng.range(12, 22);
          if (rng.chance(k)) {
            const tight = camOf(inp.frames, 'main_tight');
            const f = inp.frames.find((x) => x.camId === tight);
            // Only when it's actually tighter than the wide (same gantry, same size = JUMP CUT).
            if (f && f.framing.focusInFrame && !isWideSize(f.framing.shotSize)) {
              const r = tryCut(inp, tight);
              if (r !== 'defer' && r.length > 0) { out.push(...r); holdUntil = t + rng.range(2.5, 4); }
            }
          }
        }
      } else if (STOPPAGE.has(s.phase)) {
        // Keep the stoppage alive with reactions; amateurs sit on one shot.
        const patience = lerp(30, 8, k);
        if (t - lastCutT > patience && rng.chance(dt * lerp(0.2, 2, k))) {
          const kinds: CameraKind[] = ['pitchside_handheld_left', 'crowd', 'pitchside_handheld_right', 'bench'];
          const cam = camOf(inp.frames, kinds[stoppageRotation++ % kinds.length]!);
          const r = tryCut(inp, cam);
          if (r !== 'defer' && r.length > 0) { out.push(...r); holdUntil = t + rng.range(3, 5); }
        }
      }
      return out;
    },
  };
}
